import { useTranslation } from "react-i18next";

import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import { formatNumber } from "@memorang/helpers/common";
import type { Distribution } from "@memorang/types";
import { useAppConfig } from "../../contexts/AppConfigContext";
import { type LevelIconName, isLevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { getMasteryLevelColor } from "../MasteryChip";
import OptionListItem from "../QuickStudyModal/OptionListItem";
import TooltipWrapper from "../TooltipWrapper";

type MasteryLevel = AppConfigMasteryLevelsType[number];

export type SessionConfigFilterItem = {
	label: Record<string, string>;
	distKey: string;
	color?: string;
};

type Props = {
	selectedCheckboxMap?: Record<string, boolean>;
	handleSelectCheckbox: (checkboxState: Record<string, boolean>) => void;
	distribution: Distribution;
	disableAll?: boolean;
	hasAccess?: boolean;
	distributionFilterItems: SessionConfigFilterItem[];
	handleShowPurchase: () => void;
	hideTotalItemCounts?: boolean;
};

const fallbackIconMap: Record<string, LevelIconName> = {
	none: "minus-circle-outline",
	low: "speedometer-slow",
	medium: "speedometer-medium",
	high: "speedometer",
};

const resolveMasteryIconProps = ({
	distKey,
	fallbackColor,
	isDark,
	masteryLevel,
}: {
	distKey: string;
	fallbackColor?: string;
	isDark: boolean;
	masteryLevel?: MasteryLevel;
}): { icon?: LevelIconName; iconColor?: string } => {
	if (!masteryLevel) {
		return { icon: fallbackIconMap[distKey], iconColor: fallbackColor };
	}

	return {
		icon: isLevelIconName(masteryLevel.icon)
			? masteryLevel.icon
			: fallbackIconMap[distKey],
		iconColor: getMasteryLevelColor(masteryLevel, isDark),
	};
};

const resolveMasteryCountProps = ({
	hideTotalItemCounts,
	distKey,
	count,
	color,
	fallbackColor,
	whiteColor,
	t,
}: {
	hideTotalItemCounts?: boolean;
	distKey: string;
	count: number;
	color?: string;
	fallbackColor: string;
	whiteColor: string;
	t: ReturnType<typeof useTranslation>["t"];
}): {
	countLabel?: string;
	countBackgroundColor?: string;
	countTextColor?: string;
} => {
	if (hideTotalItemCounts && distKey === "none") {
		return {};
	}

	return {
		countLabel: `${formatNumber(count)} ${t("common.item", { count })}`,
		countBackgroundColor: color ?? fallbackColor,
		countTextColor: whiteColor,
	};
};

const DistributionFilterList = ({
	selectedCheckboxMap,
	handleSelectCheckbox,
	distribution,
	disableAll,
	hasAccess,
	distributionFilterItems,
	handleShowPurchase,
	hideTotalItemCounts,
}: Props) => {
	const { t, i18n } = useTranslation();
	const theme = useAppTheme();
	const { masteryLevels } = useAppConfig();

	const masteryColorMap: Record<string, string> = {
		none: theme.palette.progress.noneBackground,
		low: theme.palette.progress.low,
		medium: theme.palette.progress.medium,
		high: theme.palette.progress.high,
	};

	const masteryLevelMap = new Map(
		(masteryLevels ?? []).map((level) => [level.id, level]),
	);

	const interceptSelectCheckbox = (distkey: string) => {
		const newCheckboxMap = {
			...(selectedCheckboxMap || {}),
			[distkey]: !selectedCheckboxMap?.[distkey],
		};
		handleSelectCheckbox(newCheckboxMap);
	};

	const rows = distributionFilterItems.map(({ distKey, color, label }) => {
		const count = distribution[distKey] ?? 0;
		return {
			distKey,
			color,
			label,
			count,
			checked: Boolean(selectedCheckboxMap?.[distKey]),
		};
	});

	return (
		<Box gap={8}>
			{rows.map((row, index) => {
				const { distKey, color, label, count, checked } = row;
				const hasNoItems = count === 0;
				const { icon, iconColor } = resolveMasteryIconProps({
					distKey,
					fallbackColor: color ?? masteryColorMap[distKey],
					isDark: theme.dark,
					masteryLevel: masteryLevelMap.get(distKey),
				});
				const { countLabel, countBackgroundColor, countTextColor } =
					resolveMasteryCountProps({
						hideTotalItemCounts,
						distKey,
						count,
						color,
						fallbackColor: masteryColorMap[distKey],
						whiteColor: theme.colors.white,
						t,
					});
				const item = (
					<OptionListItem
						key={distKey}
						id={distKey}
						label={label?.[i18n.language] ?? t(`mastery.labels.${distKey}`)}
						icon={icon}
						iconColor={iconColor}
						countLabel={countLabel}
						countBackgroundColor={countBackgroundColor}
						countTextColor={countTextColor}
						checked={checked}
						disabled={hasNoItems || disableAll}
						locked={!hasAccess}
						testID={`mastery-checkbox-${index}`}
						onToggle={() =>
							hasAccess
								? interceptSelectCheckbox(distKey)
								: handleShowPurchase()
						}
					/>
				);
				if (hasNoItems) {
					return (
						<TooltipWrapper
							key={distKey}
							title={t("sessionConfig.noItemsTooltip")}
							display="block"
							style={{ cursor: "not-allowed" }}
						>
							{item}
						</TooltipWrapper>
					);
				}
				return item;
			})}
		</Box>
	);
};

export default DistributionFilterList;
